import React, { useEffect, useState } from "react";
import { EnvelopeSimple, Check, X, ArrowsClockwise } from "@phosphor-icons/react";
import { sendVerificationEmail, verifyEmailCode } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * VerifyEmailBanner — yellow banner shown across all authed views when the
 * current user has not yet verified their email (and didn't sign in via Google).
 *
 * On mount, auto-detects ?verify={token} in the URL and submits it immediately.
 * Otherwise the user can click "Send verification email" -> "Enter code" -> verify.
 * Dismissal is per-session (it returns next reload until the user is actually verified).
 */
export default function VerifyEmailBanner() {
  const { user, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(""); // "" | "sending" | "sent" | "verifying" | "verified" | "error"
  const [msg, setMsg] = useState("");

  // Auto-verify from URL ?verify=token=... on mount.
  // Also strips the param when the user is already verified (cleans up stale links).
  useEffect(() => {
    if (!user) return;
    try {
      const url = new URL(window.location.href);
      const token = url.searchParams.get("verify");
      if (!token) return;
      if (user.emailVerified) {
        // Stale link — just clean the URL silently
        url.searchParams.delete("verify");
        window.history.replaceState({}, "", url.toString());
        return;
      }
      (async () => {
        setStatus("verifying");
        try {
          const res = await verifyEmailCode({ token });
          if (res.verified) {
            setStatus("verified");
            setMsg("Email verified. Welcome aboard.");
            await refresh();
          }
        } catch (e) {
          setStatus("error");
          setMsg(e.response?.data?.detail || "Could not verify with that link.");
        } finally {
          // Strip ?verify from URL so refreshes don't re-attempt
          try {
            url.searchParams.delete("verify");
            window.history.replaceState({}, "", url.toString());
          } catch (e) { console.debug("clean verify url", e); }
        }
      })();
    } catch (e) { console.debug("verify url parse", e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Hold success banner visible for ~1.2s after verification so the user can read the success
  // before the parent re-render unmounts us.
  const [successHold, setSuccessHold] = useState(false);
  useEffect(() => {
    if (status === "verified") {
      setSuccessHold(true);
      const t = setTimeout(() => setSuccessHold(false), 1200);
      return () => clearTimeout(t);
    }
  }, [status]);

  if (!user || dismissed) return null;
  // Stay mounted briefly to display the success state, even after user.emailVerified flips true.
  if (user.emailVerified && !successHold) return null;

  async function handleSend() {
    setStatus("sending"); setMsg("");
    try {
      const res = await sendVerificationEmail();
      if (res.alreadyVerified) {
        setStatus("verified"); setMsg("Your email is already verified — refresh the page.");
        await refresh();
      } else if (res.sent) {
        setStatus("sent");
        setMsg(`Verification email sent to ${res.email}. Check your inbox (and spam folder).`);
        setShowCodeForm(true);
      } else {
        // sender not configured — let the user know in plain language
        setStatus("sent");
        setMsg("We created your verification code, but our email service isn't fully configured yet. Ask your admin to enter the code from the server log, or have them grant your account directly from the Admin panel.");
        setShowCodeForm(true);
      }
    } catch (e) {
      setStatus("error");
      setMsg(e.response?.data?.detail || "Could not send verification email.");
    }
  }

  async function handleVerifyCode() {
    if (!code.trim() || code.trim().length !== 6) {
      setStatus("error"); setMsg("Enter the 6-digit code from the email.");
      return;
    }
    setStatus("verifying"); setMsg("");
    try {
      const res = await verifyEmailCode({ code: code.trim() });
      if (res.verified) {
        setStatus("verified"); setMsg("Email verified.");
        await refresh();
      }
    } catch (e) {
      setStatus("error");
      setMsg(e.response?.data?.detail || "Invalid code.");
    }
  }

  // While in the success-hold window, render a celebratory green version of the banner.
  if (user.emailVerified && successHold) {
    return (
      <div className="verify-banner verify-banner--success" data-testid="verify-email-banner">
        <div className="verify-banner-icon"><Check size={18} weight="fill" /></div>
        <div className="verify-banner-body">
          <div className="verify-banner-title">Email verified</div>
          <div className="verify-banner-sub">Thanks — you're all set. Your account is now protected and password recovery is enabled.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="verify-banner" data-testid="verify-email-banner">
      <div className="verify-banner-icon"><EnvelopeSimple size={18} weight="fill" /></div>
      <div className="verify-banner-body">
        <div className="verify-banner-title">Verify your email address</div>
        <div className="verify-banner-sub">
          We'll send a 6-digit code to <strong>{user.email}</strong>. This unlocks password recovery and protects your claim data.
        </div>
        {msg && (
          <div className={`verify-banner-msg ${status === "error" ? "verify-banner-msg--err" : ""}`} data-testid="verify-email-msg">
            {msg}
          </div>
        )}
        {!showCodeForm ? (
          <div className="verify-banner-actions">
            <button
              className="btn btn--accent btn--sm"
              onClick={handleSend}
              disabled={status === "sending"}
              data-testid="verify-send-btn"
            >
              <EnvelopeSimple size={11} weight="bold" /> {status === "sending" ? "Sending..." : "Send verification email"}
            </button>
            <button
              className="btn btn--outline btn--sm"
              onClick={() => setShowCodeForm(true)}
              data-testid="verify-have-code-btn"
            >
              I have a code
            </button>
          </div>
        ) : (
          <div className="verify-banner-form">
            <input
              className="field-input verify-code-input mono"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="verify-code-input"
            />
            <button
              className="btn btn--accent btn--sm"
              onClick={handleVerifyCode}
              disabled={status === "verifying" || code.length !== 6}
              data-testid="verify-submit-btn"
            >
              <Check size={11} weight="bold" /> {status === "verifying" ? "Verifying..." : "Verify"}
            </button>
            <button
              className="btn btn--ghost btn--sm"
              onClick={handleSend}
              disabled={status === "sending"}
              title="Send a new code"
              data-testid="verify-resend-btn"
            >
              <ArrowsClockwise size={11} weight="bold" />
            </button>
          </div>
        )}
      </div>
      <button
        className="verify-banner-close"
        onClick={() => setDismissed(true)}
        title="Dismiss for this session (the banner will return on next login)"
        data-testid="verify-banner-dismiss"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
